import type { JSX } from "solid-js";
import { createEffect, createResource, createSignal, Show } from "solid-js";
import CustomerResourceService from "../../services/CustomerResourceService";
import { DECORATIVE_IMAGE_ALT } from "../../constants";
import { isNil } from "lodash-es";
import missingImage from "../../assets/missing-image.svg";

type BaseProps = {
  alt: string;
  size: "custom" | "lg" | "md" | "sm" | "xs";
  cover: boolean;
  style?: JSX.CSSProperties;
};

type RootImageProps = BaseProps & {
  src?: string;
  isLoading: boolean;
  isError: boolean;
  onLoad: () => void;
  onError: () => void;
};

export type LocalImageProps = BaseProps & {
  src: string;
};

export type RemoteImageProps = BaseProps & {
  resourceId?: string | null;
};

const RootImage = (props: RootImageProps) => (
  <div
    class="mx-auto flex w-full items-center justify-center overflow-hidden rounded-sm bg-surface-extra-subtle"
    classList={{
      "h-full": props.size === "custom",
      "h-45": props.size === "lg",
      "h-24": props.size === "md",
      "h-12": props.size === "sm",
      "h-7.5": props.size === "xs",
    }}
  >
    <Show when={props.isLoading || props.isError || isNil(props.src)}>
      <img
        class="w-full"
        classList={{
          "h-full": props.size !== "custom" && props.size !== "lg",
          "h-24": props.size === "custom" || props.size === "lg",
        }}
        src={missingImage}
        alt={DECORATIVE_IMAGE_ALT}
      />
    </Show>
    <Show when={props.src}>
      <img
        data-testid="image-preview"
        class="size-full overflow-hidden"
        style={props.style}
        src={props.src}
        aria-hidden={props.isLoading || props.isError ? true : undefined}
        classList={{
          "object-contain": !props.cover,
          "object-cover": props.cover,
          hidden: props.isLoading || props.isError,
        }}
        alt={props.alt}
        onLoad={() => props.onLoad()}
        onError={() => props.onError()}
      />
    </Show>
  </div>
);

export const LocalImage = (props: LocalImageProps) => {
  const [isLoading, setIsLoading] = createSignal(true);
  const [isError, setIsError] = createSignal(false);

  return (
    <RootImage
      {...props}
      isLoading={isLoading()}
      isError={isError()}
      onLoad={() => setIsLoading(false)}
      onError={() => setIsError(true)}
    />
  );
};

export const RemoteImage = (props: RemoteImageProps) => {
  const [data] = createResource(
    () => props.resourceId,
    async (resourceId) => {
      const secureUri = await CustomerResourceService.getSecureUri(resourceId);
      return { id: resourceId, source: secureUri };
    },
  );

  const [isLoading, setIsLoading] = createSignal(true);
  const [isError, setIsError] = createSignal(false);

  createEffect(() => {
    if (isNil(props.resourceId) && !isError()) {
      setIsLoading(true);
      setIsError(false);
    }
  });

  return (
    <RootImage
      {...props}
      src={!isNil(props.resourceId) && !isNil(data()?.source) ? data()?.source : undefined}
      isLoading={isLoading()}
      isError={isError()}
      onLoad={() => setIsLoading(false)}
      onError={() => setIsError(true)}
    />
  );
};
